// RegenAction — Sprint 19BH Day 1 Step 1.4 · regen workflow command surface.
//
// Why this exists (v4-rewrite.md §10):
//   Regen is the v4 product loop core (K14 promotion from "escape hatch" to
//   "primary product surface"). Every user action that mutates an ingest
//   package post-Phase-5 goes through one of the 4 RegenAction variants:
//
//     - delete       : remove items matching scope · no LLM call
//     - replace      : LLM regenerates the matching items (optional feedback)
//     - add          : LLM adds N more items matching scope
//     - fill_audit_gap: shorthand · internally translates to `add` with
//                       scope derived from the referenced GapEntry
//
//   Phase 1 (POST /ingest/regenerate) gathers context from
//   manifest.regenContext, calls LLM (or no-op for delete), and returns
//   { regenId, proposedItems, beforeSnapshot, diffSummary }. The user
//   reviews the diff in the frontend, then Phase 2
//   (POST /ingest/regenerate-confirm) applies or rejects.
//
// Why discriminated union by `kind`:
//   The 4 actions differ in payload shape (delete has no count; replace
//   accepts userFeedback; add carries count + crossChapterAffinity;
//   fill_audit_gap references a GapEntry id). Union by `kind` keeps
//   per-variant type strictness and lets regen-workflow.ts switch
//   exhaustively (compile-time guard against unhandled variants).
//
// Adding a new RegenAction kind (extensibility checkpoint §12.4):
//   1. Add the variant schema below
//   2. Add a switch case in regen-workflow.ts (Day 4 Step 4.4)
//   That's 2 files total — 0 frontend changes (UI dispatches by kind).
import { z } from 'zod';
import { StageIdSchema } from './stage-id.js';
import { OUTLINE_ID_PATTERN, OUTLINE_ID_MESSAGE } from './curriculum-outline.js';
const OutlineId = () => z.string().regex(OUTLINE_ID_PATTERN, OUTLINE_ID_MESSAGE);
/**
 * Scope of items targeted by a RegenAction. All locus fields optional —
 * absence means "no constraint" (e.g. scope `{ stage: 'mcqs' }` matches all
 * mcq items in the package). Used by regen-workflow.ts to build the LLM
 * context bundle and by frontend to mutate the affected slice.
 */
export const RegenScopeSchema = z.object({
    /** Required: which stage's output is being regenerated. */
    stage: StageIdSchema
        .describe('Stage whose items the action affects.'),
    /** Limit to specific chapters. */
    caseIds: z.array(OutlineId()).optional()
        .describe('Optional chapter id filter. Items whose caseIds[] intersects this list match.'),
    /** Limit to specific topics. */
    topicIds: z.array(OutlineId()).optional()
        .describe('Optional topic id filter.'),
    /** Limit to specific concepts. */
    conceptIds: z.array(OutlineId()).optional()
        .describe('Optional concept id filter.'),
    /** Limit to specific item ids (for delete / replace by id). */
    itemIds: z.array(z.string().min(1).max(120)).optional()
        .describe('Optional explicit item ids. Used for delete/replace of specific items the user flagged.'),
}).passthrough();
// ─── 4 variants ─────────────────────────────────────────────────────────────
const DeleteActionSchema = z.object({
    kind: z.literal('delete'),
    scope: RegenScopeSchema,
}).passthrough();
const ReplaceActionSchema = z.object({
    kind: z.literal('replace'),
    scope: RegenScopeSchema,
    /** Optional user hint to bias the regeneration (e.g. "make the distractors closer"). */
    userFeedback: z.string().max(500).optional()
        .describe('Optional user feedback to the LLM. 1–500 chars (empty allowed). Reaches the regen prompt verbatim.'),
}).passthrough();
/** When add scope spans multiple chapters, controls how multi-chapter the generated items can be. */
export const CrossChapterAffinitySchema = z.enum(['single', 'natural', 'preferred']);
const AddActionSchema = z.object({
    kind: z.literal('add'),
    scope: RegenScopeSchema,
    /** How many new items to generate. */
    count: z.number().int().min(1).max(50)
        .describe('Number of items to add (1–50). Upper bound is a sanity cap; typical use is 1–10.'),
    /** Multi-chapter generation affinity (subjective stages only). */
    crossChapterAffinity: CrossChapterAffinitySchema.optional()
        .describe('How willing the LLM should be to emit multi-chapter caseIds. Default: stage-manifest-driven.'),
    /** Optional prompt augment. */
    userPromptHint: z.string().max(500).optional()
        .describe('Optional user prompt hint. Augments the regen prompt with target style / focus.'),
}).passthrough();
const FillAuditGapActionSchema = z.object({
    kind: z.literal('fill_audit_gap'),
    /** ULID of the GapEntry being addressed. */
    gapEntryId: z.string().min(1).max(64)
        .describe('GapEntry id (ULID) from a GapReport.entries[]. Workflow dereferences this to derive scope.'),
}).passthrough();
/**
 * Discriminated union of regen action variants.
 *
 * IMPORTANT (avoiding z.lazy in GapEntry.suggestedAction): this schema is
 * defined and exported here so audit-gap-entry.ts can import it directly
 * without z.lazy. Per plan §6 risk-mitigation note, Step 1.4 lands BEFORE
 * Step 1.3 in commit order to break the forward-ref cycle.
 */
export const RegenActionSchema = z.discriminatedUnion('kind', [
    DeleteActionSchema,
    ReplaceActionSchema,
    AddActionSchema,
    FillAuditGapActionSchema,
]);
/** Convenience: all 4 kinds as a const tuple, used by tests + UI dispatchers. */
export const REGEN_ACTION_KINDS = [
    'delete',
    'replace',
    'add',
    'fill_audit_gap',
];
//# sourceMappingURL=regen-action.js.map